import { useEffect } from 'react'

import { PaymentMethodEnum } from 'src/Constants/enums'
import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { useDefaultPaymentMethod } from 'src/Hooks/useDefaultPaymentMethod'
import { useGetBillingAgreementQuery } from 'src/Redux/billingAgreementApi'
import { setPaymentMethod } from 'src/Redux/payment-process'
import { getPaymentMethod } from 'src/Redux/payment-process/selectors'
import { changeRememberPayPalForFutureFlag } from 'src/Redux/paypalTransactions-process'
import { getRememberPayPalForFutureFlag } from 'src/Redux/paypalTransactions-process/selectors'
import { CampaignPlatformTypeEnum, CampaignTypeEnum } from 'src/Types/campaigns'
import {
  getIsEnoughBalanceSelector,
  spotifyTotalPriceSelector,
} from 'src/Redux/spotifyCampaignMatchingProcess'
import { paymentMethodChanged } from 'src/Helpers/TagManager'
import { getAmountOfCampaigns } from 'src/Redux/campaignList-process'
import { useSpotifyCampaignStatus } from 'src/Redux/spotifyCampaignStatusApi'
import { useGetSubscriptionQuery } from 'src/Redux/subscription-process/subscriptionApi'
import { getUserId } from 'src/Redux/auth-process/userSlice/selectors'
import {
  getCountry,
  isCountryLoaded,
} from 'src/Redux/auth-process/countrySlice/selectors'
import { getUserCountry } from 'src/Redux/auth-process/countrySlice/api-actions'

type UseSpotifySchedulePaymentMethod = {
  onChange: (value: PaymentMethodEnum) => void
  onChangeHasSavePaypal: (value: boolean) => void
  hasSavePaypal: boolean
  paymentMethod: PaymentMethodEnum
  paypalEmail: string | undefined
  isEnoughBalance: boolean
  countryCode: string
}

export const useSpotifySchedulePaymentMethod =
  (): UseSpotifySchedulePaymentMethod => {
    const dispatch = useAppDispatch()
    const { data: plan } = useGetSubscriptionQuery()
    const { data: billingData } = useGetBillingAgreementQuery()
    const userId = useAppSelector(getUserId)
    const { campaignDetails: scheduledSpotifyCampaign } =
      useSpotifyCampaignStatus()
    const spotifyTotalPrice = useAppSelector(spotifyTotalPriceSelector)
    const amountOfCampaigns = useAppSelector(getAmountOfCampaigns)
    const paymentMethod = useAppSelector(getPaymentMethod)
    const isRememberPayPalForFuture = useAppSelector(
      getRememberPayPalForFutureFlag,
    )
    const countryCode = useAppSelector(getCountry)
    const countryLoaded = useAppSelector(isCountryLoaded)

    useEffect(() => {
      if (!countryLoaded) {
        dispatch(getUserCountry())
      }
    }, [dispatch, countryLoaded])

    const isEnoughBalance = useAppSelector(getIsEnoughBalanceSelector)
    useDefaultPaymentMethod({
      isEnoughBalance,
      isMetaUrl: false,
      hasBillingData: Boolean(billingData?.paypalEmail),
    })

    const handleChangePaymentMethod = (value: PaymentMethodEnum): void => {
      paymentMethodChanged({
        campaignId: Number(scheduledSpotifyCampaign?.id),
        campaignNumber: amountOfCampaigns,
        campaignPlatform: CampaignPlatformTypeEnum.SPOTIFY,
        campaignType: scheduledSpotifyCampaign?.type || CampaignTypeEnum.BASIC,
        genreList:
          scheduledSpotifyCampaign?.genres?.map((genre) => genre.name).join() ||
          '',
        paymentMethod: value,
        prevPaymentMethod: paymentMethod,
        resultPrice: spotifyTotalPrice || 0,
        userId: Number(userId),
        subscriptionPlan: plan?.package?.name || '',
      })
      dispatch(setPaymentMethod(value))

      localStorage.setItem('currentPaymentMethod', value)
    }
    const handleChangeHasSavePaypal = (value: boolean): void => {
      dispatch(changeRememberPayPalForFutureFlag(value))
    }
    return {
      countryCode,
      paymentMethod,
      isEnoughBalance,
      hasSavePaypal: isRememberPayPalForFuture,
      paypalEmail: billingData?.paypalEmail,
      onChangeHasSavePaypal: handleChangeHasSavePaypal,
      onChange: handleChangePaymentMethod,
    }
  }
